/**
 * Pattern C session: wallet + signature → { didId, eligibleUntil }.
 * A connected address alone is not a login.
 *
 * Applicant claims carry a live Global DID id.
 * Assigned-admin claims use kind: 'admin' and didId 0 — no APTMDAO NFT
 * or Global Standard DID is required to operate Admin.
 */

export const WEB3_SESSION_STORAGE_KEY = 'sf.web3.session';
export const DEFAULT_CLAIM_TTL_MS = 15 * 60 * 1000;

export type Web3ClaimKind = 'did' | 'admin' | 'wallet';

export interface Web3Claim {
  address: string;
  didId: number;
  kind?: Web3ClaimKind;
  issuedAt: number;
  eligibleUntil: number;
  message: string;
  signature: string;
}

export interface BuildLoginMessageInput {
  domain: string;
  address: string;
  uri: string;
  chainId: number;
  nonce: string;
  issuedAt: string;
  statement?: string;
}

export const ADMIN_LOGIN_STATEMENT =
  'Sign in to SportsFuture as an assigned admin. This proves you control the wallet. No APTMDAO NFT or Global Standard DID is required.';

export const WALLET_LOGIN_STATEMENT =
  'Sign in to SportsFuture. This proves you control the connected wallet. A Global DID is required later to submit an application.';

export function buildLoginMessage(input: BuildLoginMessageInput): string {
  const statement =
    input.statement ??
    'Sign in to SportsFuture with your Apertum Global DID. This proves you control the wallet.';
  return [
    `${input.domain} wants you to sign in with your Apertum account:`,
    input.address,
    '',
    statement,
    '',
    `URI: ${input.uri}`,
    'Version: 1',
    `Chain ID: ${input.chainId}`,
    `Nonce: ${input.nonce}`,
    `Issued At: ${input.issuedAt}`,
  ].join('\n');
}

export function randomNonce(bytes = 16): string {
  const arr = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function issueClaim(opts: {
  address: string;
  didId?: number;
  kind?: Web3ClaimKind;
  message: string;
  signature: string;
  now?: number;
  ttlMs?: number;
}): Web3Claim {
  const now = opts.now ?? Date.now();
  const kind = opts.kind ?? 'did';
  return {
    address: opts.address.toLowerCase(),
    didId: opts.didId ?? 0,
    kind,
    issuedAt: now,
    eligibleUntil: now + (opts.ttlMs ?? DEFAULT_CLAIM_TTL_MS),
    message: opts.message,
    signature: opts.signature,
  };
}

export function isClaimValid(
  claim: Web3Claim | null | undefined,
  now = Date.now()
): claim is Web3Claim {
  if (!claim) return false;
  if (!claim.address || !claim.signature || !claim.message) return false;
  if (now >= claim.eligibleUntil) return false;
  if (claim.kind === 'admin' || claim.kind === 'wallet') return true;
  if (!Number.isFinite(claim.didId) || claim.didId <= 0) return false;
  return true;
}

export function recoveredSignerMatches(
  recovered: string,
  expected: string
): boolean {
  return recovered.toLowerCase() === expected.toLowerCase();
}

export function readStoredClaim(
  storage: Pick<Storage, 'getItem'> | null = defaultStorage()
): Web3Claim | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(WEB3_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Web3Claim;
    return isClaimValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeStoredClaim(
  claim: Web3Claim,
  storage: Pick<Storage, 'setItem'> | null = defaultStorage()
): void {
  if (!storage) return;
  storage.setItem(WEB3_SESSION_STORAGE_KEY, JSON.stringify(claim));
}

export function clearStoredClaim(
  storage: Pick<Storage, 'removeItem'> | null = defaultStorage()
): void {
  if (!storage) return;
  storage.removeItem(WEB3_SESSION_STORAGE_KEY);
}

function defaultStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
